// --------------------------------
// Copyright (c) 2023 Eco8 S.r.l.
// --------------------------------
import React, { useState, useRef, useEffect } from "react";
import {
    View,
    Text,
    TouchableOpacity,
    TextInput,
    TouchableWithoutFeedback,
} from "react-native";
import colors from "../../../config/colors";
import styles from "../../../styles/Carrier/TransportCardStyle";
import ComboPicker from "../Farmer/Movements/Elements/ComboPicker";
import { arrivedRequest, startRequest } from "../../../axios/axiosConfig";
import { useNavigation } from "@react-navigation/native";
import {
    validateSubstance,
    getContainerBackgroundColor,
    getDividerColor,
    getTitleColor,
    getStatusIconMinus,
    getStatusIconPlus,
    renderRoute,
} from "../../../utils/transportCardUtils";
import {
    fetchVechicleList,
} from "../../../axios/axiosConfig";
import { Console } from "winston/lib/winston/transports";

const TransportCard = ({
    transportId,
    status,
    plannedDate,
    getTranslation,
    qta,
    sub,
    addressFrom,
    addressTo,
    MaterialName,
    //plates,
    handleShowSnackbar,
    plannedQuantity,
    handleReload,
    //defaultVehicle,
    ownerFromType,
    ownerTypeTo,
    emptyForContentType,
    fetchActiveRequests,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [quantity, setQuantity] = useState("");
    const [substance, setSubstance] = useState(null);
    const [vehicles, setVehicles] = useState(null);
    const [defaultVehicle, setDefaultVehicle] = useState(null);
    const [vehicleId, setVehicleId] = useState(defaultVehicle);
    const [vehicleChanged, setVehicleChanged] = useState(false);
    const quantityInputRef = useRef(null);
    const substanceInputRef = useRef(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const navigation = useNavigation();
    const logger = require("../../../axios/loggingConfig");

    useEffect(() => {
        fetchVehiclePlates(MaterialName);
    }, []);

    const fetchVehiclePlates = async(contentType) => {
        try {
            if(contentType == "Empty"){
                contentType = emptyForContentType;
            }
            const vehicleResponse = await fetchVechicleList(contentType);
            if (vehicleResponse.status === 200) {
                const vehiclesData = vehicleResponse.data;
                setVehicles(vehiclesData);
                if (vehiclesData.length > 0) {
                    setDefaultVehicle(vehiclesData[0].id);
                    setVehicleId(vehiclesData[0].id);
                }
            }
        } catch (error){
            console.log(error);
        }
    };

    const handleSubmit = async () => {
        let response, formattedSubstance, loadedQuantity;
        setExpanded(false);
        try {
            fetchActiveRequests();
            if (status === "Warning" || status === "OnGoing") {
                formattedSubstance = validateSubstance(substance);
                loadedQuantity = quantity || plannedQuantity;
                response = await arrivedRequest(
                    transportId,
                    loadedQuantity,
                    formattedSubstance
                );
                if (response.status === 200) {
                    handleShowSnackbar(
                        getTranslation("Tratta conclusa correttamente"),
                        colors.green
                    );
                    if (response.data.isGroupCompleted == true) {
                        navigation.navigate(getTranslation("Richieste"));
                    }
                } else {
                    throw response;
                }
            } else {
                loadedQuantity = quantity || plannedQuantity;
                response = await startRequest(transportId, loadedQuantity, vehicleId);
                if (response.status === 200) {
                    handleShowSnackbar(
                        getTranslation("Tratta avviata correttamente"),
                        colors.green
                    );
                } else {
                    throw response;
                }
            }
            handleReload();
        } catch (error) {
            const statusCode = error?.response?.status;
            const errorMsg = error?.response?.data || "Unknown error";
            handleShowSnackbar(getTranslation(errorMsg), colors.red);
            logger.error(
                `Error in ${status === "Warning" || status === "OnGoing"
                    ? "arrivedRequest"
                    : "startRequest"
                } in TransportCard.js. ${statusCode ? `Status: ${statusCode}` : ""}`,
                { category: "API" }
            );
        }
    };

    const handlePress = () => {
        setExpanded(!expanded);
    };

    const handlePickerChange = (value) => {
        setVehicleId(value);
        setVehicleChanged(true);
    };

    const handleQuantityPress = () => {
        if (!expanded) {
            setExpanded(true);
            setTimeout(() => {
                quantityInputRef.current.focus();
            }, 200);
        }
    };

    const handleSubstancePress = () => {
        if (!expanded) {
            setExpanded(true);
            setTimeout(() => {
                const initialValue = substance?.replace(".", "") || "";
                setSubstance(initialValue);
                substanceInputRef.current.setSelection({
                    start: initialValue.length,
                    end: initialValue.length,
                });
                substanceInputRef.current.focus();
            }, 50);
        }
    };

    const handleSubstanceChange = (text) => {
        setSubstance(text);
    };

    const handleQuantityChange = (text) => {
        setQuantity(text);
    };

    return (
        <TouchableOpacity onPress={handlePress}>
            <View
                style={[
                    styles.container,
                    expanded && styles.expandedContainer,
                    { backgroundColor: getContainerBackgroundColor(status) },
                ]}
            >
                <View style={styles.header}>
                    <View style={styles.plusIcon}>
                        {expanded ? (
                            <Text>{getStatusIconMinus(status)}</Text>
                        ) : (
                            <Text>{getStatusIconPlus(status)}</Text>
                        )}
                    </View>
                    <View style={styles.firstCont}>
                        {renderRoute(
                            status,
                            addressFrom,
                            addressTo,
                            ownerFromType,
                            ownerTypeTo
                        )}
                    </View>
                </View>
                {expanded && (
                    <View style={styles.details}>
                        <View style={styles.secondCont}>
                            <View style={styles.textContainer}>
                                <Text
                                    style={[styles.text, { color: getTitleColor(status) }]}
                                    numberOfLines={3}
                                    ellipsizeMode="tail"
                                >
                                    {addressFrom?.address + "\n"}
                                </Text>
                            </View>
                            <View style={styles.textContainer}>
                                <Text
                                    style={[styles.text, { color: getTitleColor(status) }]}
                                    numberOfLines={3}
                                    ellipsizeMode="tail"
                                >
                                    {addressTo?.address + "\n"}
                                </Text>
                            </View>
                        </View>
                        <View
                            style={[
                                styles.horizontalDivider,
                                { backgroundColor: getDividerColor(status) },
                            ]}
                        />
                        <View style={{ paddingVertical: 10 }}>

                            {emptyForContentType !== null ? (
                                <View>
                                    <Text style={[styles.text, { color: getTitleColor(status) }]}>
                                        {getTranslation("Material")}
                                    </Text>
                                    <Text style={[styles.text, { color: getTitleColor(status) }]}>
                                        {getTranslation("Vuoto") +
                                            "=>" +
                                            getTranslation(emptyForContentType)}
                                    </Text>
                                </View>
                            ) : (
                                <View>
                                    <Text style={[styles.text, { color: getTitleColor(status) }]}>
                                        {getTranslation("Material")}
                                    </Text>
                                    <Text style={[styles.text, { color: getTitleColor(status) }]}>
                                        {getTranslation(MaterialName)}
                                    </Text>
                                </View>
                            )}

                            <Text style={[styles.text, { color: getTitleColor(status) }]}>
                                {getTranslation("Qta. Pianificata [Kg]")}
                            </Text>
                            <Text style={[styles.text, { color: getTitleColor(status) }]}>
                                {plannedQuantity}
                            </Text>

                            {status === "Confirmed" && (
                                <View>
                                    <Text
                                        style={[
                                            styles.text,
                                            { fontSize: 15, color: getTitleColor(status) },
                                        ]}>
                                        {getTranslation("Targa Automezzo")}
                                    </Text>
                                    <TouchableWithoutFeedback>
                                        <View>
                                            <ComboPicker
                                                items={vehicles}
                                                plates={true}
                                                onValueChange={handlePickerChange}
                                            />
                                        </View>
                                    </TouchableWithoutFeedback>
                                </View>
                            )}

                        </View>


                        {ownerFromType !== 1 && status !== "Confirmed" ? (
                            <View style={[styles.details, styles.expandedDetails]}>
                                <View style={{ marginBottom: 5 }}>
                                    <View>
                                        <Text
                                            style={[styles.text, { color: getTitleColor(status) }]}
                                        >
                                            {getTranslation("Quantità [Kg]")}
                                        </Text>
                                        {status === "Completed" || (status === "OnGoing" && MaterialName === "Empty") ? (
                                            <Text
                                                style={[
                                                    styles.text,
                                                    { fontSize: 24, color: getTitleColor(status) },
                                                ]}
                                            >
                                                {qta}
                                            </Text>
                                        ) : (
                                            <TouchableWithoutFeedback onPress={handleQuantityPress}>
                                                <View>
                                                    <TextInput
                                                        style={styles.input}
                                                        placeholder={"_____________"}
                                                        value={quantity}
                                                        keyboardType="numeric"
                                                        onChangeText={handleQuantityChange}
                                                        ref={quantityInputRef}
                                                        maxLength={5}
                                                        required
                                                    />
                                                    {formSubmitted && quantity === "" && (
                                                        <Text style={styles.warningText}>
                                                            {getTranslation("Attenzione arrivo senza pesata")}
                                                        </Text>
                                                    )}
                                                </View>
                                            </TouchableWithoutFeedback>
                                        )}
                                    </View>
                                </View>
                                <View style={{ marginBottom: 5 }}>
                                    <View>
                                        <Text
                                            style={[styles.text, { color: getTitleColor(status) }]}
                                        >
                                            {getTranslation("Sostanza Secca [%]")}
                                        </Text>
                                        {status === "Completed" || (status === "OnGoing" && MaterialName === "Empty") ? (
                                            <Text
                                                style={[
                                                    styles.text,
                                                    { fontSize: 24, color: getTitleColor(status) },
                                                ]}
                                            >
                                                {sub}
                                            </Text>
                                        ) : (
                                            <TouchableWithoutFeedback onPress={handleSubstancePress}>
                                                <View>
                                                    <TextInput
                                                        style={styles.input}
                                                        placeholder="_____________"
                                                        keyboardType="numeric"
                                                        onChangeText={handleSubstanceChange}
                                                        ref={substanceInputRef}
                                                        maxLength={4}
                                                        required
                                                    />
                                                </View>
                                            </TouchableWithoutFeedback>
                                        )}
                                    </View>
                                </View>
                                <View></View>
                            </View>
                        ) : (
                            <View>
                                {status === "Completed" ||
                                    status === "OnGoing" ||
                                    status === "Warning" ? (
                                    <View>
                                        <Text
                                            style={[
                                                styles.text,
                                                { fontSize: 24, color: getTitleColor(status) },
                                            ]}
                                        >
                                            {getTranslation("Quantità [Kg]")}:
                                        </Text>
                                        <Text
                                            style={[
                                                styles.text,
                                                { fontSize: 30, color: getTitleColor(status) },
                                            ]}
                                        >
                                            {qta}
                                        </Text>
                                    </View>
                                ) : (
                                    ownerFromType === 1 &&
                                    emptyForContentType === null && (
                                        <View>
                                            <TouchableWithoutFeedback onPress={handleQuantityPress}>
                                                <View>
                                                    <Text
                                                        style={[
                                                            styles.text,
                                                            { fontSize: 15, color: getTitleColor(status) },
                                                        ]}
                                                    >
                                                        {getTranslation("Quantità [Kg]")}
                                                    </Text>
                                                    <TextInput
                                                        style={styles.input}
                                                        placeholder={"_____________"}
                                                        value={quantity}
                                                        keyboardType="numeric"
                                                        onChangeText={handleQuantityChange}
                                                        ref={quantityInputRef}
                                                        maxLength={5}
                                                        required
                                                    />
                                                    {formSubmitted && quantity === "" && (
                                                        <Text style={styles.warningText}>
                                                            {getTranslation(
                                                                "Attenzione partenza senza carico"
                                                            )}
                                                        </Text>
                                                    )}
                                                </View>
                                            </TouchableWithoutFeedback>
                                        </View>
                                    )
                                )}
                            </View>
                        )}
                        {status === "Completed" ? (
                            <Text
                                style={[
                                    styles.buttonText,
                                    {
                                        backgroundColor: getContainerBackgroundColor(),
                                        fontSize: 31,
                                        alignContent: "center",
                                        textAlign: "center",
                                    },
                                ]}
                            >
                                {getTranslation("Consegnato")}
                            </Text>
                        ) : (
                            <TouchableOpacity
                                style={[
                                    styles.button,
                                    status === "Completed" && {
                                        backgroundColor: colors.greenWater,
                                    },
                                ]}
                                onPress={handleSubmit}
                            >
                                {status === "Completed" ? null : status === "OnGoing" ||
                                    status === "Warning" ? (
                                    <Text style={[styles.buttonText, { fontSize: 18 }]}>
                                        {getTranslation("Arrivo")}
                                    </Text>
                                ) : (
                                    status === "Confirmed" && (
                                        <Text
                                            style={[
                                                styles.buttonText,
                                                { fontSize: 18, alignContent: "center" },
                                            ]}
                                        >
                                            {getTranslation("Partenza")}
                                        </Text>
                                    )
                                )}
                            </TouchableOpacity>
                        )}
                    </View>
                )}
            </View>
        </TouchableOpacity>
    );
};

export default TransportCard;
